<template>
  <div class="subscription-select-container">
    <b-loading :is-full-page="true" v-model="loading"></b-loading>

    <!-- Header Section -->
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">Choose Your Plan</h1>
        <p class="hero-subtitle">Select the plan that's right for your team</p>
      </div>
    </div>

    <div class="main-content">
      <!-- Plan Selection Section -->
      <div class="section-card feature-card">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-th-large"></i>
            Basic Plans
          </h2>
          <p class="section-description">Select your base inquiry plan</p>
        </div>

        <div class="plans-grid">
          <div v-for="plan in filteredOptions" :key="plan.id" class="plan-card-wrapper">
            <SubscriptionOption
              :is-active="false"
              :is-selected="selectedPlanId === plan.id"
              :plan="plan"
              @click="selectPlan"
              class="enhanced-plan-option"
            />
          </div>
        </div>
      </div>

      <!-- Blanket VOB Feature Section -->
      <div class="section-card feature-card">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-search-plus"></i>
            Blanket VOB Search
          </h2>
          <a @click="showBlanketVOBModal = !showBlanketVOBModal" class="feature-link">
            <i class="fas fa-info-circle"></i>
            See details
          </a>
        </div>

        <div class="feature-content">
          <div class="feature-toggle">
            <b-switch
              size="is-large"
              @input="handleBlanketVOBToggle($event)"
              v-model="hasBlanketVOB"
              type="is-success"
            >
              <span class="toggle-label">Enable Blanket VOB Search</span>
            </b-switch>
          </div>

          <!-- Blanket VOB Plan Selection -->
          <div v-if="hasBlanketVOB" class="blanket-vob-section">
            <div class="blanket-plans-grid">
              <div
                v-for="plan in filteredBlanketVOBOptions"
                :key="plan.id"
                class="blanket-plan-card"
                :class="{
                  'is-selected': selectedBlanketVOBPlanId === plan.id,
                }"
                @click="selectBlanketVOBPlan(plan.id)"
              >
                <div class="card-content">
                  <div class="media">
                    <div class="media-content has-text-centered">
                      <p class="title is-4">{{ plan.name }}</p>
                      <p class="subtitle is-6">${{ plan.base_cost }}/mo</p>
                    </div>
                  </div>

                  <div class="content">
                    <div class="mt-2 has-text-centered">
                      <span v-if="selectedBlanketVOBPlanId === plan.id" class="has-text-primary">
                        <i class="fas fa-dot-circle"></i>
                      </span>
                    </div>

                    <ul>
                      <li>
                        {{ plan.base_inquiries }} Inquiries
                        <small class="has-text-grey">(${{ plan.addl_cost }} per extra)</small>
                      </li>
                      <li>Unlimited Users</li>
                      <li>Unlimited Providers/Facilities</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reimbursement Feature Section -->
      <div class="section-card feature-card">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-chart-line"></i>
            Reimbursement Estimates
          </h2>
          <a @click="showModal = !showModal" class="feature-link">
            <i class="fas fa-info-circle"></i>
            See details
          </a>
        </div>

        <div class="feature-content">
          <div class="feature-text">
            <p>Access historical payer data for informed reimbursement decisions.</p>
          </div>

          <div class="feature-toggle">
            <b-switch size="is-large" v-model="hasFeatureReimbursement" type="is-info">
              <span class="toggle-label">
                Enable Reimbursement Estimates
                <span v-if="filteredReimbursementOptions.length > 0">
                  (${{ filteredReimbursementOptions[0].base_cost }}/month)
                </span>
              </span>
            </b-switch>
          </div>
        </div>
      </div>

      <!-- Bundle Promo Message -->
      <div v-if="selectedFeaturesCount === 2" class="bundle-promo-message">
        <i class="fas fa-gift"></i>
        Bundle 3 features to unlock a 10% discount each month!
      </div>

      <!-- Bundle Discount Applied -->
      <div v-else-if="selectedFeaturesCount === 3" class="bundle-discount-applied">
        <i class="fas fa-check-circle"></i>
        <span class="discount-text">Applying 10% discount</span>
        <span class="divider">•</span>
        <span class="original-price">${{ estimatedMonthlyPrice }}</span>
        <i class="fas fa-arrow-right arrow-icon"></i>
        <span class="discounted-price">${{ discountedMonthlyPrice }}/month</span>
      </div>

      <!-- Action Buttons -->
      <div class="action-section">
        <div class="action-buttons">
          <b-button
            type="is-primary"
            size="is-large"
            @click="handleSelect"
            :disabled="!selectedPlanId"
            class="get-started-button"
          >
            <i class="fas fa-rocket"></i>
            <span>Get Started</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <b-modal v-model="showModal" :width="640" scroll="keep">
      <reimbursement-feature-detail></reimbursement-feature-detail>
    </b-modal>

    <b-modal v-model="showBlanketVOBModal" :width="640" scroll="keep">
      <BlanketVOBFeatureDetail></BlanketVOBFeatureDetail>
    </b-modal>
  </div>
</template>

<script>
import request from '../request';
import SubscriptionOption from './SubscriptionOption';
import ReimbursementFeatureDetail from './ReimbursementFeatureDetail';
import BlanketVOBFeatureDetail from './BlanketVOBFeatureDetail';

export default {
  name: 'SubscriptionSelect',
  components: {
    SubscriptionOption,
    ReimbursementFeatureDetail,
    BlanketVOBFeatureDetail,
  },
  data() {
    return {
      loading: false,
      options: [],
      blanketVOBOptions: [],
      reimbursementOptions: [],
      hasFeatureReimbursement: false,
      hasBlanketVOB: false,
      selectedPlanId: null,
      selectedBlanketVOBPlanId: null,
      showModal: false,
      showBlanketVOBModal: false,
    };
  },
  computed: {
    filteredOptions() {
      return this.options.filter((opt) => opt.is_enabled);
    },
    filteredBlanketVOBOptions() {
      return this.blanketVOBOptions.filter((opt) => opt.is_enabled);
    },
    filteredReimbursementOptions() {
      return this.reimbursementOptions.filter((opt) => opt.is_enabled);
    },
    selectedFeaturesCount() {
      let count = 0;
      if (this.selectedPlanId) count++;
      if (this.hasBlanketVOB && this.selectedBlanketVOBPlanId) count++;
      if (this.hasFeatureReimbursement) count++;
      return count;
    },
    estimatedMonthlyPrice() {
      let total = 0;

      // Add base plan price
      const selectedBasicPlan = this.options.find((p) => p.id === this.selectedPlanId);
      if (selectedBasicPlan) {
        const price = parseFloat(selectedBasicPlan.base_cost.replace(/,/g, ''));
        total += price;
      }

      // Add Blanket VOB price if selected
      if (this.hasBlanketVOB && this.selectedBlanketVOBPlanId) {
        const selectedBlanketVOBPlan = this.blanketVOBOptions.find(
          (p) => p.id === this.selectedBlanketVOBPlanId
        );
        if (selectedBlanketVOBPlan) {
          const price = parseFloat(selectedBlanketVOBPlan.base_cost.replace(/,/g, ''));
          total += price;
        }
      }

      // Add Reimbursement price if selected
      if (this.hasFeatureReimbursement) {
        const reimbPlan = this.reimbursementOptions.find((plan) => plan.is_enabled);
        if (reimbPlan) {
          const price = parseFloat(reimbPlan.base_cost.replace(/,/g, ''));
          total += price;
        }
      }

      return total.toFixed(2);
    },
    discountedMonthlyPrice() {
      const originalPrice = parseFloat(this.estimatedMonthlyPrice);
      const discountedPrice = originalPrice * 0.9; // 10% discount
      return discountedPrice.toFixed(2);
    },
  },
  mounted() {
    this.loading = true;
    request.get('/plans').then((resp) => {
      this.options = resp.data.plans.basic_inquiries_plans;
      this.blanketVOBOptions = resp.data.plans.blanket_vob_plans;
      this.reimbursementOptions = resp.data.plans.feature_reimbursement_plans || [];
      this.loading = false;
    });
  },
  methods: {
    selectPlan(plan) {
      this.selectedPlanId = plan.id;
    },
    selectBlanketVOBPlan(planId) {
      this.selectedBlanketVOBPlanId = planId;
    },
    handleBlanketVOBToggle(event) {
      this.hasBlanketVOB = event;
      // If turning off, clear selected plan
      if (!event) {
        this.selectedBlanketVOBPlanId = null;
      } else {
        // If turning on and no plan is selected, automatically select the first enabled plan
        if (!this.selectedBlanketVOBPlanId && this.filteredBlanketVOBOptions.length > 0) {
          this.selectedBlanketVOBPlanId = this.filteredBlanketVOBOptions[0].id;
        }
      }
    },
    handleSelect() {
      if (!this.selectedPlanId) return;
      this.submit();
    },
    submit() {
      this.loading = true;

      // Prepare the checkout payload
      const payload = {
        plan_id: this.selectedPlanId,
      };

      // Add blanket VOB plan if selected
      if (this.hasBlanketVOB && this.selectedBlanketVOBPlanId) {
        payload.blanket_vob_plan_id = this.selectedBlanketVOBPlanId;
      }

      // Add reimbursement plan if selected - use the first enabled plan
      if (this.hasFeatureReimbursement) {
        const enabledReimbursementPlan = this.reimbursementOptions.find((plan) => plan.is_enabled);
        if (enabledReimbursementPlan) {
          payload.reimbursement_plan_id = enabledReimbursementPlan.id;
        }
      }

      request
        .post('/subscriptions/checkout-session', payload)
        .then((resp) => {
          window.location.href = resp.data.redirect_url;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style>
/* Override parent section padding specifically for subscription select */
.subscription-select-container {
  margin-top: -3rem !important; /* Bulma section default padding is 3rem */
}
</style>

<style scoped>
.subscription-select-container {
  min-height: 100vh;
  background: none;
  padding: 0;
  margin: 0;
  margin-top: -0.5rem;
  position: relative;
  z-index: 1;
}

.hero-section {
  background: #ffffff;
  color: #1f2937;
  padding: 0;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative !important;
  top: -1.5rem !important;
  z-index: 10;
}

.hero-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 0.5rem 2rem;
}

.hero-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.hero-subtitle {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 400;
  margin: 0;
}

.main-content {
  background: #f8fafc;
  min-height: calc(100vh - 120px);
  padding: 0 2rem 2rem 2rem;
  margin-top: 0;
  border-radius: 0;
  position: relative;
  z-index: 1;
}

.section-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.15);
}

.feature-card {
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  color: #667eea;
  font-size: 1.5rem;
}

.section-description {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.plan-card-wrapper {
  transition: transform 0.3s ease;
}

.plan-card-wrapper:hover {
  transform: translateY(-4px);
}

.feature-content {
  display: flex;
  flex-direction: column;
  gap: 0rem;
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.feature-text p {
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.feature-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.feature-link:hover {
  color: #4f46e5;
}

.feature-toggle {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.feature-toggle:hover {
  border-color: #d1d5db;
}

.toggle-label {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
}

.blanket-vob-section {
  background: none;
  padding: 2rem 0;
  border: none;
}

.blanket-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.blanket-plan-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 0;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow:
    0 2px 3px rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.1);
}

.blanket-plan-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

.blanket-plan-card.is-selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
}

.bundle-promo-message {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 1px solid #10b981;
  border-radius: 0.75rem;
  padding: 1.25rem 1.75rem;
  margin: 0.5rem 0 1rem 0;
  text-align: center;
  font-size: 0.9375rem;
  color: #065f46;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.12);
  line-height: 1.5;
}

.bundle-promo-message i {
  color: #10b981;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.bundle-discount-applied {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 1px solid #10b981;
  border-radius: 0.75rem;
  padding: 1.25rem 1.75rem;
  margin: 0.5rem 0 1rem 0;
  text-align: center;
  font-size: 0.9375rem;
  color: #065f46;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.12);
  line-height: 1.5;
}

.bundle-discount-applied i.fa-check-circle {
  color: #10b981;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.bundle-discount-applied .discount-text {
  color: #065f46;
}

.bundle-discount-applied .divider {
  color: #10b981;
  font-weight: 400;
}

.bundle-discount-applied .original-price {
  color: #6b7280;
  text-decoration: line-through;
  font-weight: 500;
}

.bundle-discount-applied .arrow-icon {
  color: #10b981;
  font-size: 0.75rem;
}

.bundle-discount-applied .discounted-price {
  color: #065f46;
  font-weight: 700;
}

.action-section {
  background: transparent;
  border-radius: 0;
  padding: 2rem 0;
  text-align: center;
  box-shadow: none;
  border: none;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.get-started-button {
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  border: none !important;
  border-radius: 1rem !important;
  padding: 1.5rem 3rem !important;
  font-weight: 700 !important;
  font-size: 1.3rem !important;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
  transition: all 0.3s ease !important;
  min-width: 300px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.get-started-button:hover:not(:disabled) {
  transform: translateY(-3px) !important;
  box-shadow: 0 15px 35px rgba(102, 126, 234, 0.5) !important;
  background: linear-gradient(135deg, #5a67d8, #6b46c1) !important;
}

.get-started-button:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.get-started-button i {
  margin-right: 0.75rem;
  font-size: 1.2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .main-content {
    padding: 1rem;
  }

  .section-card {
    padding: 1.5rem;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .blanket-plans-grid {
    grid-template-columns: 1fr;
  }

  .get-started-button {
    width: 100% !important;
  }
}
</style>
